import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { API_BASE_URL } from '../../config';
import StatusHistoryTimeline from '../../Components/StatusHistoryTimeline';
import TeacherDiaryPanel, { hasTeacherDiary } from '../../Components/TeacherDiaryPanel';
import {
  BookOpen,
  Users,
  Calendar,
  Clock,
  Search,
  Filter,
  Eye,
  FileText,
  TrendingUp,
  BarChart3,
  PieChart,
  User,
  CheckCircle,
  AlertCircle,
  Edit,
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  GraduationCap,
  Target,
  Layers,
  Brain,
  Settings,
  Play,
  ShieldCheck,
  AlertTriangle,
  XCircle,
  Upload
} from 'lucide-react';

const LessonPlanManagement = () => {
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data state
  const [stats, setStats] = useState({
    totalPlans: 0,
    draftPlans: 0,
    taughtPlans: 0,
    archivedPlans: 0,
    plansThisWeek: 0,
    plansThisMonth: 0,
    averagePerTeacher: 0,
    topDomains: [],
    topGrades: [],
    teacherStats: []
  });
  const [lessonPlans, setLessonPlans] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [totalPlans, setTotalPlans] = useState(0);

  // Filter and pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    domain: '',
    grade: '',
    teacher: '',
    startDate: '',
    endDate: '',
    updatedStartDate: '',
    updatedEndDate: ''
  });
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);

  // API base URL imported from config

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchLessonPlans();
  }, [currentPage, filters]);

  // API helper function
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // Fetch initial data
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [statsData, teachersData] = await Promise.all([
        apiCall('/teacher/lesson-plans/admin/stats'),
        apiCall('/teacher/lesson-plans/admin/teachers/list')
      ]);

      setStats(statsData);
      setTeachers(teachersData);
    } catch (err) {
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch lesson plans with filters
  const fetchLessonPlans = async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          const apiKey = key === 'status' ? 'status_filter' :
            key === 'domain' ? 'domain_filter' :
              key === 'grade' ? 'grade_filter' :
                key === 'teacher' ? 'teacher_filter' :
                  key === 'startDate' ? 'start_date' :
                    key === 'endDate' ? 'end_date' :
                      key === 'updatedStartDate' ? 'updated_start_date' :
                        key === 'updatedEndDate' ? 'updated_end_date' : key;
          params.append(apiKey, value);
        }
      });

      const data = await apiCall(`/teacher/lesson-plans/admin/all?${params.toString()}`);
      setLessonPlans(data.lessonPlans || []);
      setTotalPlans(data.total || 0);
    } catch (err) {
      setError(`Failed to fetch lesson plans: ${err.message}`);
    }
  };

  // View lesson plan details
  const viewLessonPlan = async (planId) => {
    try {
      const data = await apiCall(`/teacher/lesson-plans/admin/${planId}`);
      setSelectedPlan(data);
      setShowPlanModal(true);
    } catch (err) {
      setError(`Failed to load lesson plan: ${err.message}`);
    }
  };

  // Deep-link support: HODDashboard's review modal opens
  // `/LessonPlanManagement?planId=<id>` in a new tab so a reviewer can read
  // the full lesson plan before deciding, without losing their place in the
  // review dialog. Auto-open that plan's detail modal on load.
  useEffect(() => {
    const planId = new URLSearchParams(window.location.search).get('planId');
    if (planId) {
      viewLessonPlan(planId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      domain: '',
      grade: '',
      teacher: '',
      startDate: '',
      endDate: '',
      updatedStartDate: '',
      updatedEndDate: ''
    });
    setCurrentPage(1);
  };

  // Show notification
  const showNotification = (type, message) => {
    if (type === 'success') {
      setSuccess(message);
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(message);
      setTimeout(() => setError(null), 5000);
    }
  };

  // Get status color
  // Full status set from the gated approval workflow (Teacher -> HOD ->
  // Admin) — see Backend/app/services/lesson_plan_workflow.py. Previously
  // only draft/taught/archived were mapped here, so every plan awaiting
  // review or approved rendered with the (wrong) draft styling.
  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-yellow-100 text-yellow-800',
      pending_approval: 'bg-amber-100 text-amber-800',
      pending_admin: 'bg-orange-100 text-orange-800',
      changes_requested: 'bg-purple-100 text-purple-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-700',
      taught: 'bg-indigo-100 text-indigo-800',
      published: 'bg-teal-100 text-teal-800',
      archived: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.draft;
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      pending_approval: 'Awaiting HOD Review',
      pending_admin: 'Awaiting Admin Review',
      changes_requested: 'Changes Requested',
      approved: 'Approved',
      rejected: 'Rejected',
      taught: 'Taught',
      published: 'Published',
      archived: 'Archived',
    };
    return labels[status] || status;
  };

  // Get status icon
  const getStatusIcon = (status) => {
    const icons = {
      draft: <Edit className="w-4 h-4" />,
      pending_approval: <Clock className="w-4 h-4" />,
      pending_admin: <ShieldCheck className="w-4 h-4" />,
      changes_requested: <AlertTriangle className="w-4 h-4" />,
      approved: <CheckCircle className="w-4 h-4" />,
      rejected: <XCircle className="w-4 h-4" />,
      taught: <CheckCircle className="w-4 h-4" />,
      published: <Upload className="w-4 h-4" />,
      archived: <FileText className="w-4 h-4" />
    };
    return icons[status] || icons.draft;
  };

  // Render stats cards
  const renderStats = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Lesson Plans</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">{stats.totalPlans || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-blue-100">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Plans This Month</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{stats.plansThisMonth || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-green-100">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Taught Plans</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">{stats.taughtPlans || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-purple-100">
              <CheckCircle className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Avg per Teacher</p>
              <p className="text-3xl font-bold text-orange-600 mt-1">{stats.averagePerTeacher || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-orange-100">
              <Users className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render analytics section
  const renderAnalytics = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
      {/* Top Domains */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-100">
            <PieChart className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Top Domains</h3>
        </div>
        <div className="space-y-3">
          {stats.topDomains?.slice(0, 5).map((domain, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{domain.name}</span>
              <span className="text-sm font-semibold text-gray-900">{domain.count}</span>
            </div>
          ))}
          {stats.topDomains?.length === 0 && (
            <p className="text-sm text-gray-500">No data available</p>
          )}
        </div>
      </div>

      {/* Top Grades */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-100">
            <BarChart3 className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Top Grades</h3>
        </div>
        <div className="space-y-3">
          {stats.topGrades?.slice(0, 5).map((grade, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{grade.name}</span>
              <span className="text-sm font-semibold text-gray-900">{grade.count}</span>
            </div>
          ))}
          {stats.topGrades?.length === 0 && (
            <p className="text-sm text-gray-500">No data available</p>
          )}
        </div>
      </div>

      {/* Teacher Performance */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-100">
            <Users className="w-5 h-5 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Top Teachers</h3>
        </div>
        <div className="space-y-3">
          {stats.teacherStats?.slice(0, 5).map((teacher, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{teacher.teacherName}</span>
              <span className="text-sm font-semibold text-gray-900">{teacher.totalPlans}</span>
            </div>
          ))}
          {stats.teacherStats?.length === 0 && (
            <p className="text-sm text-gray-500">No data available</p>
          )}
        </div>
      </div>
    </div>
  );

  // Helper functions for rendering lesson plan content
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Render rich text content safely
  const renderRichText = (content) => {
    if (!content) return <span className="text-gray-500 italic">Not specified</span>;

    // If content contains HTML tags, render as sanitized HTML
    if (typeof content === 'string' && content.includes('<')) {
      return <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />;
    }

    // Otherwise render as plain text with line breaks
    return <div className="prose prose-sm max-w-none whitespace-pre-wrap">{content}</div>;
  };

  // Render competencies
  const renderCompetencies = (competencies) => {
    if (!competencies || competencies.length === 0) {
      return <span className="text-gray-500 italic">No competencies selected</span>;
    }

    return (
      <div className="space-y-2">
        {competencies.map((comp, index) => (
          <div key={index} className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400">
            <div className="font-semibold text-blue-900">{comp}</div>
          </div>
        ))}
      </div>
    );
  };

  // Render lesson flow table for Panchaadi method
  const renderPanchaadiLessonFlow = (lessonFlowData) => {
    if (!lessonFlowData || !Array.isArray(lessonFlowData) || lessonFlowData.length === 0) {
      return <span className="text-gray-500 italic">No lesson flow data</span>;
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Stage</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Time</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Teacher Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Student Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Activity Examples</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resources</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {lessonFlowData.map((item, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-300">{item.stage || 'N/A'}</td>
                <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-300">{item.time || 'N/A'}</td>
                <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-300">{item.teacherRole || 'N/A'}</td>
                <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-300">{item.studentRole || 'N/A'}</td>
                <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-300">{item.activityExamples || 'N/A'}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{item.resources || 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render observation checklist
  const renderObservationChecklist = (checklist) => {
    if (!checklist) {
      return <span className="text-gray-500 italic">No observation checklist</span>;
    }

    if (typeof checklist === 'string') {
      return (
        <div className="space-y-2">
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex-shrink-0 w-5 h-5 bg-blue-100 rounded border border-blue-300 mt-0.5"></div>
            <span className="text-gray-700">{checklist}</span>
          </div>
        </div>
      );
    }

    if (Array.isArray(checklist)) {
      if (checklist.length === 0) {
        return <span className="text-gray-500 italic">No observation checklist items</span>;
      }

      return (
        <div className="space-y-2">
          {checklist.map((item, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0 w-5 h-5 bg-blue-100 rounded border border-blue-300 mt-0.5"></div>
              <span className="text-gray-700">{item}</span>
            </div>
          ))}
        </div>
      );
    }

    if (typeof checklist === 'object') {
      const entries = Object.entries(checklist);
      if (entries.length === 0) {
        return <span className="text-gray-500 italic">No observation checklist items</span>;
      }

      return (
        <div className="space-y-2">
          {entries.map(([key, value], index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0 w-5 h-5 bg-blue-100 rounded border border-blue-300 mt-0.5"></div>
              <span className="text-gray-700">
                <strong>{key}:</strong> {value}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
          <div className="flex-shrink-0 w-5 h-5 bg-blue-100 rounded border border-blue-300 mt-0.5"></div>
          <span className="text-gray-700">{String(checklist)}</span>
        </div>
      </div>
    );
  };

  // Render "Skills to Inculcate" — a free-form multi-select of 21st-century
  // skills (see Teacher/src/Pages/LessonPlanner/skillsToInculcate.js),
  // stored as pedagogicalPrinciples.selectedSkills. This replaced the
  // earlier fixed set of six pedagogical-principle checkboxes.
  const renderPedagogicalPrinciples = (principles) => {
    const skills = principles?.selectedSkills;
    if (!Array.isArray(skills) || skills.length === 0) {
      return <span className="text-gray-500 italic">No skills selected</span>;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {skills.map((skill) => (
          <div key={skill} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">{skill}</span>
          </div>
        ))}
      </div>
    );
  };

  // Render comprehensive lesson plan modal
  const renderLessonPlanModal = () => {
    if (!showPlanModal || !selectedPlan) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex justify-center items-start p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl mt-4 mb-4">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#D67C2C] to-[#c06a25] rounded-t-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Eye className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl font-bold mb-1">{selectedPlan.themeTopic || 'Untitled Lesson Plan'}</h1>
                  <div className="flex items-center gap-4 text-white text-opacity-90">
                    <span className="text-sm">by {selectedPlan.teacherName}</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedPlan.status)}`}>
                      {getStatusIcon(selectedPlan.status)}
                      {getStatusLabel(selectedPlan.status)}
                    </span>
                    <span className="text-sm">Created: {formatDate(selectedPlan.createdAt)}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowPlanModal(false)}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[80vh] overflow-y-auto space-y-6">

            {/* Review status — both approval stages, whichever have happened */}
            {(selectedPlan.hod_feedback || selectedPlan.reviewed_by || selectedPlan.admin_feedback || selectedPlan.admin_reviewed_by) && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <ShieldCheck className="w-6 h-6 text-[#D67C2C]" />
                  Review Status
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {selectedPlan.reviewed_by && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">HOD Stage — {selectedPlan.reviewed_by}</p>
                      {selectedPlan.reviewed_at && (
                        <p className="text-xs text-gray-400 mb-1">{formatDate(selectedPlan.reviewed_at)}</p>
                      )}
                      {selectedPlan.hod_feedback && (
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedPlan.hod_feedback}</p>
                      )}
                    </div>
                  )}
                  {selectedPlan.admin_reviewed_by && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Admin Stage — {selectedPlan.admin_reviewed_by}</p>
                      {selectedPlan.admin_reviewed_at && (
                        <p className="text-xs text-gray-400 mb-1">{formatDate(selectedPlan.admin_reviewed_at)}</p>
                      )}
                      {selectedPlan.admin_feedback && (
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedPlan.admin_feedback}</p>
                      )}
                    </div>
                  )}
                </div>
                {Array.isArray(selectedPlan.changes_since_approval) && selectedPlan.changes_since_approval.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm font-medium text-indigo-700">Changed since it was last approved:</p>
                    <p className="text-sm text-indigo-600">{selectedPlan.changes_since_approval.map(c => c.field).join(', ')}</p>
                  </div>
                )}
              </div>
            )}

            {/* Activity History — every status change this plan has been
                through: draft, submit, HOD/admin decisions, resets, taught. */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Clock className="w-6 h-6 text-[#D67C2C]" />
                Activity History
              </h2>
              <StatusHistoryTimeline history={selectedPlan.status_history} />
            </div>

            {/* General Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <FileText className="w-6 h-6 text-[#D67C2C]" />
                General Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">Domain</h3>
                  </div>
                  <p className="text-gray-600">{selectedPlan.domain || 'N/A'}</p>
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <GraduationCap className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-gray-900">Grade</h3>
                  </div>
                  <p className="text-gray-600">Grade {selectedPlan.classGrade || 'N/A'}</p>
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-gray-900">Lesson Date</h3>
                  </div>
                  <p className="text-gray-600">{formatDate(selectedPlan.lessonDate)}</p>
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="w-5 h-5 text-orange-600" />
                    <h3 className="font-semibold text-gray-900">Duration</h3>
                  </div>
                  <p className="text-gray-600">{selectedPlan.duration || 'N/A'}</p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-3">Topic/Theme</h3>
                  <p className="text-gray-600">{selectedPlan.themeTopic || 'N/A'}</p>
                </div>
                <div className="mt-4">
                  <h3 className="font-semibold text-gray-700 mb-3">Curricular Goal</h3>
                  {renderRichText(selectedPlan.curricularGoal)}
                </div>
              </div>
            </div>

            {/* Competencies */}
            {selectedPlan.competencies && selectedPlan.competencies.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <Layers className="w-6 h-6 text-[#D67C2C]" />
                  Competencies
                </h2>
                {renderCompetencies(selectedPlan.competencies)}
              </div>
            )}

            {/* Learning Objectives (renamed from "Learning Outcomes" — keep in
                sync with Teacher/src/Pages/LessonPlanner/LessonPlanComponent.js) */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Target className="w-6 h-6 text-[#D67C2C]" />
                Learning Objectives
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Learning Objective 1</h3>
                  {renderRichText(selectedPlan.learningOutcomes?.outcome1 || selectedPlan.outcome1)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Learning Objective 2</h3>
                  {renderRichText(selectedPlan.learningOutcomes?.outcome2 || selectedPlan.outcome2)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Learning Objective 3</h3>
                  {renderRichText(selectedPlan.learningOutcomes?.outcome3 || selectedPlan.outcome3)}
                </div>
              </div>
            </div>

            {/* Alternative Objectives — field is alternativeObjectives on the
                document; selectedPlan.altObjectives never existed, so this
                section was always empty regardless of what the teacher wrote. */}
            {selectedPlan.alternativeObjectives && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <Target className="w-6 h-6 text-[#D67C2C]" />
                  Alternative Objectives
                </h2>
                {renderRichText(selectedPlan.alternativeObjectives)}
              </div>
            )}

            {/* Skills to Inculcate (renamed from "Pedagogical Principles to
                Apply") */}
            {selectedPlan.pedagogicalPrinciples && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <Brain className="w-6 h-6 text-[#D67C2C]" />
                  Skills to Inculcate
                </h2>
                {renderPedagogicalPrinciples(selectedPlan.pedagogicalPrinciples)}
              </div>
            )}

            {/* Differentiation & Inclusion */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Users className="w-6 h-6 text-[#D67C2C]" />
                Differentiation & Inclusion
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Advanced Learners</h3>
                  {renderRichText(selectedPlan.inclusionStrategies?.advancedLearners || selectedPlan.advancedLearners)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Struggling Learners</h3>
                  {renderRichText(selectedPlan.inclusionStrategies?.strugglingLearners || selectedPlan.strugglingLearners)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Group Strategy</h3>
                  {renderRichText(selectedPlan.inclusionStrategies?.groupStrategy || selectedPlan.groupStrategy)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Special Needs</h3>
                  {renderRichText(selectedPlan.inclusionStrategies?.specialNeeds || selectedPlan.specialNeeds)}
                </div>
              </div>
              {selectedPlan.alternativeSlowLearner && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-700 mb-2">Slow Learners Support</h3>
                  {renderRichText(selectedPlan.alternativeSlowLearner)}
                </div>
              )}
            </div>

            {/* Classroom Management */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Settings className="w-6 h-6 text-[#D67C2C]" />
                Classroom Management
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Seating Arrangement</h3>
                  {renderRichText(selectedPlan.classroomManagement?.seatingArrangement || selectedPlan.seatingArrangement)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Material Setup</h3>
                  {renderRichText(selectedPlan.classroomManagement?.materialSetup || selectedPlan.materialSetup)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Noise Control</h3>
                  {renderRichText(selectedPlan.classroomManagement?.noiseControl || selectedPlan.noiseControl)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Rules & Routines</h3>
                  {renderRichText(selectedPlan.classroomManagement?.rulesRoutines || selectedPlan.rulesRoutines)}
                </div>
              </div>
            </div>

            {/* Lesson Flow */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Play className="w-6 h-6 text-[#D67C2C]" />
                Lesson Flow - {selectedPlan.lessonMethod === 'panchaadi' ? '5-Step Panchaadi Method' : 'Alternative Method'}
              </h2>

              {selectedPlan.lessonMethod === 'panchaadi' ? (
                selectedPlan.lessonFlowData ? (
                  renderPanchaadiLessonFlow(selectedPlan.lessonFlowData)
                ) : (
                  <span className="text-gray-500 italic">No Panchaadi lesson flow data available</span>
                )
              ) : (
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Alternative Lesson Flow</h3>
                  {renderRichText(selectedPlan.alternativeLessonFlow)}
                </div>
              )}
            </div>

            {/* Resources and Materials — alternative-method fields; the
                Panchaadi method has no separate resource/worksheet fields. */}
            {(selectedPlan.alternativeResourceMaterial || selectedPlan.alternativeWorksheet) && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <FileText className="w-6 h-6 text-[#D67C2C]" />
                  Resources and Materials
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {selectedPlan.alternativeResourceMaterial && (
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-2">Resource Material</h3>
                      {renderRichText(selectedPlan.alternativeResourceMaterial)}
                    </div>
                  )}
                  {selectedPlan.alternativeWorksheet && (
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-2">Worksheet</h3>
                      {renderRichText(selectedPlan.alternativeWorksheet)}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Lesson Notes & Documentation */}
            {(selectedPlan.content || selectedPlan.richTextContent) && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <FileText className="w-6 h-6 text-[#D67C2C]" />
                  Lesson Notes & Documentation
                </h2>
                {renderRichText(selectedPlan.richTextContent || selectedPlan.content)}
              </div>
            )}

            {/* Reflection (renamed from "Assessment") */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-[#D67C2C]" />
                Reflection
              </h2>

              {/* Observation Checklist */}
              {selectedPlan.assessment?.observationChecklist && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-700 mb-3">Observation Checklist</h3>
                  {renderObservationChecklist(selectedPlan.assessment.observationChecklist)}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {selectedPlan.assessment?.anecdotalNotes && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Anecdotal Notes</h3>
                    {renderRichText(selectedPlan.assessment.anecdotalNotes)}
                  </div>
                )}
                {selectedPlan.assessment?.portfolioEvidence && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Portfolio Evidence</h3>
                    {renderRichText(selectedPlan.assessment.portfolioEvidence)}
                  </div>
                )}
                {selectedPlan.assessment?.selfReflection && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Self Reflection</h3>
                    {renderRichText(selectedPlan.assessment.selfReflection)}
                  </div>
                )}
              </div>
            </div>

            {/* Teacher's Diary — objectives achieved, reflection and per-student
                feedback, recorded when the teacher marked the lesson as taught. */}
            {(selectedPlan.status === 'taught' || hasTeacherDiary(selectedPlan)) && (
              <TeacherDiaryPanel plan={selectedPlan} />
            )}

            {/* Footer Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowPlanModal(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalPlans / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalPlans);

  return (
    <>
      <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

          {/* Notifications */}
          {error && (
            <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 bg-red-500 text-white" style={{ animation: 'slideInRight 0.5s ease-out' }}>
              <AlertCircle className="w-5 h-5" />
              <span className="font-[poppins]">{error}</span>
              <button onClick={() => setError(null)} className="ml-2 hover:opacity-80">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {success && (
            <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 bg-green-500 text-white" style={{ animation: 'slideInRight 0.5s ease-out' }}>
              <CheckCircle className="w-5 h-5" />
              <span className="font-[poppins]">{success}</span>
              <button onClick={() => setSuccess(null)} className="ml-2 hover:opacity-80">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Header */}
          <div className="pt-8 pb-8 text-center relative overflow-hidden">
            <div className="relative z-10">
              <div>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: '#D67C2C' }}>
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-5xl font-bold bg-[#D67C2C] bg-clip-text text-transparent mb-4">
                  Lesson Plan Management
                </h1>
                <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                  Monitor and analyze teacher lesson plans across your institution
                </p>
              </div>
            </div>
          </div>

          {/* Dashboard Stats */}
          {renderStats()}

          {/* Analytics Section */}
          {renderAnalytics()}

          {/* Filters Section */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                  <Filter className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Filter & Search</h2>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      placeholder="Search by topic, domain, grade..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  >
                    <option value="">All Status</option>
                    <option value="draft">Draft</option>
                    <option value="pending_approval">Awaiting HOD Review</option>
                    <option value="pending_admin">Awaiting Admin Review</option>
                    <option value="changes_requested">Changes Requested</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="taught">Taught</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Teacher</label>
                  <select
                    value={filters.teacher}
                    onChange={(e) => handleFilterChange('teacher', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  >
                    <option value="">All Teachers</option>
                    {teachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Actions</label>
                  <button
                    onClick={clearFilters}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Domain</label>
                  <input
                    type="text"
                    value={filters.domain}
                    onChange={(e) => handleFilterChange('domain', e.target.value)}
                    placeholder="Filter by domain"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lesson Date From</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lesson Date To</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Updated From</label>
                  <input
                    type="date"
                    value={filters.updatedStartDate}
                    onChange={(e) => handleFilterChange('updatedStartDate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Updated To</label>
                  <input
                    type="date"
                    value={filters.updatedEndDate}
                    onChange={(e) => handleFilterChange('updatedEndDate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Lesson Plans Table */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">All Lesson Plans</h2>
                </div>
                <div className="text-sm text-gray-600">
                  Showing {startItem}-{endItem} of {totalPlans} plans
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Topic & Teacher</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain & Grade</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Duration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {lessonPlans.map((plan) => (
                    <tr key={plan.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{plan.themeTopic}</div>
                          <div className="text-sm text-gray-500 flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {plan.teacherName}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{plan.domain}</div>
                        <div className="text-sm text-gray-500">Grade {plan.classGrade}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {plan.lessonDate}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {plan.duration}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(plan.status)}`}>
                          {getStatusIcon(plan.status)}
                          {getStatusLabel(plan.status)}
                        </span>
                        {plan.taught_at && (
                          <div className="mt-1 text-xs text-gray-500 flex items-center gap-1" title="Teacher's Diary recorded">
                            <BookOpen className="w-3 h-3" />
                            Diary · {new Date(plan.taught_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(plan.updatedAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => viewLessonPlan(plan.id)}
                          className="inline-flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {lessonPlans.length === 0 && (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium text-gray-500">No lesson plans found</p>
                  <p className="text-sm text-gray-400">Try adjusting your filters or search criteria</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {startItem} to {endItem} of {totalPlans} results
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                              ? 'bg-[#D67C2C] text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Lesson Plan Detail Modal */}
        {renderLessonPlanModal()}

        {/* Modal Styles */}
        <style jsx>{`
          .prose {
            max-width: none;
          }
          .prose p {
            margin-bottom: 0.75rem;
          }
          .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
            margin-top: 1rem;
            margin-bottom: 0.5rem;
            font-weight: 600;
          }
          .prose ul, .prose ol {
            margin: 0.75rem 0;
            padding-left: 1.5rem;
          }
          .prose li {
            margin-bottom: 0.25rem;
          }
          .prose strong {
            font-weight: 600;
          }
          .prose em {
            font-style: italic;
          }
          .prose blockquote {
            border-left: 4px solid #3b82f6;
            padding-left: 1rem;
            margin: 1rem 0;
            font-style: italic;
            background: #f0f9ff;
            padding: 0.75rem 1rem;
            border-radius: 0.5rem;
          }
          .prose code {
            background: #f1f5f9;
            padding: 0.125rem 0.25rem;
            border-radius: 0.25rem;
            font-size: 0.875rem;
          }
          .prose pre {
            background: #1f2937;
            color: #f9fafb;
            padding: 1rem;
            border-radius: 0.5rem;
            overflow-x: auto;
            margin: 1rem 0;
          }
          .prose a {
            color: #3b82f6;
            text-decoration: underline;
          }
          .prose a:hover {
            color: #1d4ed8;
          }
          .prose img {
            max-width: 100%;
            height: auto;
            border-radius: 0.5rem;
            margin: 1rem 0;
          }
          .prose table {
            width: 100%;
            border-collapse: collapse;
            margin: 1rem 0;
          }
          .prose th, .prose td {
            border: 1px solid #e5e7eb;
            padding: 0.5rem;
            text-align: left;
          }
          .prose th {
            background: #f9fafb;
            font-weight: 600;
          }
        `}</style>
      </div>
    </>
  );
};

export default LessonPlanManagement;